/**
 * Tela nova do CCO (protótipo aprovado v4): Clássico, 3D e Apresentação, com zoom do macro ao micro — Nexora Grid.
 *
 * Criado em: 18/10/2026 · Última modificação: 25/10/2026 · Versão: 1.3.0
 *
 * GUS-1508/1504/1506. Decisão do responsável do produto (18/10/2026): esta tela substitui a antiga.
 * - Barra: modos CLÁSSICO · 3D · APRESENTAÇÃO (o modo fica guardado por usuário), Congelar e Movimento reduzido.
 *   A trilha mostra o caminho e a dica. US-2306: sem o "Simular anomalia" (Bonito e o robô saíram; POC só Costa Rica).
 * - Níveis com rotas próprias:
 *   - cliente: `/cco`;
 *   - cidade: `/cco/cidade/:municipio`;
 *   - setor: `/cco/unidade/:unidade`;
 *   - equipamento: `/cco/equipamento/:device`;
 *   - diagnóstico: `/cco/equipamento/:device/diagnostico`.
 *   Os links de Alarmes, Rede e SCADA 3D continuam valendo.
 * - Navegação do protótipo:
 *   - rolar sobre um card aproxima; rolar para trás, Esc ou Backspace afasta;
 *   - clique e Enter também aproximam;
 *   - fora dos cards, a roda rola a página na Clássica e aproxima a câmera na 3D.
 * - Transição na Clássica: voo pela profundidade, que parte do card escolhido. No zoom da cidade, as outras cidades saem
 *   antes. Com Movimento reduzido, a transição é só um esmaecer.
 * - Apresentação: tour roteirizado na 3D com legenda, em 8 passos que pausam, avançam, voltam e saem. É só leitura e
 *   não faz manobra.
 */
import { useCallback, useEffect, useLayoutEffect, useRef, useState, type ReactNode } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useSessao } from "../sessao";
import { CardEquip, CardSetor, ChipNota, SeloFonte, CorpoDiag, DetalheEquip, Hud, PainelEquip, blocosCidade, blocosTendencia, urlCidade, urlEquip, urlSetor, useTendencias } from "./Cards";
import { Cco3d } from "./Cco3d";
import { TIPO_ICONE, TIPO_NOME, useModelo, type Modelo } from "./dados";
import { Faixas } from "./Faixas";
import "./cco-novo.css";

type Modo = "classico" | "3d" | "apresentacao";
type Vista = { nivel: number; cidade?: string; setor?: string; equip?: string };
const CURVA = "cubic-bezier(.2,.75,.2,1)";

function lerReduzido(): boolean {
  try {
    const salvo = window.localStorage.getItem("nexora.cco.movimentoReduzido");
    if (salvo !== null) return salvo === "1";
  } catch { /* sem armazenamento */ }
  return window.matchMedia?.("(prefers-reduced-motion: reduce)").matches ?? false;
}
function guardar(chave: string, valor: string) { try { window.localStorage.setItem(chave, valor); } catch { /* sem armazenamento */ } }

/** Caminhos da tela nova (o layout do CCO a mostra em vez do Outlet: uma instância só entre os níveis). */
export const CAMINHO_CCO_NOVO = /^\/cco(?:\/(?:cidade\/[^/]+|unidade\/[^/]+|equipamento\/[^/]+(?:\/diagnostico)?))?\/?$/;

/** Nível atual pela rota. */
function useVista(modelo: Modelo): Vista {
  const { pathname } = useLocation();
  const [, tipo, codigo] = pathname.split("/").filter(Boolean);
  const p: Record<string, string | undefined> = { device: tipo === "equipamento" ? decodeURIComponent(codigo) : undefined,
    unidade: tipo === "unidade" ? decodeURIComponent(codigo) : undefined, municipio: tipo === "cidade" ? decodeURIComponent(codigo) : undefined };
  if (p.device) {
    const e = modelo.equipamento(p.device);
    return { nivel: pathname.endsWith("/diagnostico") ? 4 : 3, equip: p.device, setor: e?.setor, cidade: e?.cidade };
  }
  if (p.unidade) return { nivel: 2, setor: p.unidade, cidade: modelo.setor(p.unidade)?.cidade };
  if (p.municipio) return { nivel: 1, cidade: p.municipio };
  return { nivel: 0 };
}
const urlDe = (v: Vista): string => (v.nivel >= 4 ? `${urlEquip(v.equip!)}/diagnostico` : v.nivel === 3 ? urlEquip(v.equip!) : v.nivel === 2 ? urlSetor(v.setor!)
  : v.nivel === 1 ? urlCidade(v.cidade!) : "/cco");
/** Nível de um endereço da tela nova (para o sentido do voo). */
const nivelDaUrl = (url: string): number => (url.endsWith("/diagnostico") ? 4 : url.includes("/equipamento/") ? 3 : url.includes("/unidade/") ? 2 : url.includes("/cidade/") ? 1 : 0);
/** Nível de cima pela URL atual (null no nível do cliente). */
function acimaDaUrl(pathname: string, m: Modelo): string | null {
  const [, tipo, codigo] = pathname.split("/").filter(Boolean);
  const c = codigo ? decodeURIComponent(codigo) : "";
  if (pathname.endsWith("/diagnostico")) return urlEquip(c);
  if (tipo === "equipamento") { const e = m.equipamento(c); return e ? urlSetor(e.setor) : "/cco"; }
  if (tipo === "unidade") { const st = m.setor(c); return st ? urlCidade(st.cidade) : "/cco"; }
  if (tipo === "cidade") return "/cco";
  return null;
}

// ---------------------------------------------------------------- níveis da Clássica
function NivelCidade({ modelo, codigo }: { modelo: Modelo; codigo?: string }) {
  const c = modelo.cidade(codigo);
  const tendencias = useTendencias(codigo);
  if (!c) return <p className="aviso">carregando a cidade…</p>;
  return (
    <>
      <div className="cabeca-nivel"><h2>{c.municipio.nome}</h2><span className="suave">{c.municipio.uf} · {c.setores.length} setores
        {c.municipio.simulado && " · simulação"}</span><SeloFonte fontes={c.municipio.fontes} /><ChipNota nota={c.nota} /></div>
      <div className="blocos-cidade">{blocosCidade(c)}</div>
      <h3 className="secao-titulo">Tendências</h3><div className="tendencias">{blocosTendencia(tendencias)}</div>
      <h3 className="secao-titulo">Setores (anomalias primeiro)</h3>
      <div className="grade">{c.setores.map((s) => <CardSetor key={s.codigo} s={s} modelo={modelo} />)}</div>
    </>
  );
}

function NivelSetor({ modelo, codigo }: { modelo: Modelo; codigo?: string }) {
  const s = modelo.setor(codigo);
  if (!s) return <p className="aviso">carregando o setor…</p>;
  const c = modelo.cidade(s.cidade);
  return (
    <>
      <div className="cabeca-nivel"><h2>{s.titulo}</h2><span className="suave">{c?.municipio.nome} · {s.equipamentos.length} equipamentos</span><ChipNota nota={s.nota} /></div>
      <Hud ind={s.ind} titulo="Indicadores do setor" />
      <div className="grade">{s.equipamentos.map((e) => <CardEquip key={e.device} e={e} modelo={modelo} />)}</div>
    </>
  );
}

function NivelEquip({ modelo, device, diag }: { modelo: Modelo; device?: string; diag: boolean }) {
  const e = modelo.equipamento(device);
  if (!e) return <p className="aviso">sem dados recentes deste equipamento</p>;
  const c = modelo.cidade(e.cidade);
  return (
    <>
      <div className="cabeca-nivel"><h2>{TIPO_ICONE[e.tipo]} {e.nome}{diag && " · diagnóstico"}</h2>
        <span className="suave">{TIPO_NOME[e.tipo]} · {e.setor.toUpperCase()} · {c?.municipio.nome}</span><ChipNota nota={e.nota} normal="normal" /></div>
      {diag ? <div className="painel"><CorpoDiag e={e} modelo={modelo} completo /></div> : <><PainelEquip e={e} modelo={modelo} /><DetalheEquip e={e} /></>}
    </>
  );
}

function Plano({ vista, modelo, congelado, reduzido }: { vista: Vista; modelo: Modelo; congelado: boolean; reduzido: boolean }) {
  if (vista.nivel === 0) return <><Hud ind={modelo.cliente} titulo="Indicadores do cliente" /><Faixas modelo={modelo} congelado={congelado} reduzido={reduzido} /></>;
  if (vista.nivel === 1) return <NivelCidade modelo={modelo} codigo={vista.cidade} />;
  if (vista.nivel === 2) return <NivelSetor modelo={modelo} codigo={vista.setor} />;
  return <NivelEquip modelo={modelo} device={vista.equip} diag={vista.nivel >= 4} />;
}

// ---------------------------------------------------------------- Apresentação
type Passo = { texto: string; acao: (ctx: CtxTour) => void | Promise<void> };
type CtxTour = { modelo: Modelo; ir: (url: string) => void; alvo: { device?: string } };
const piorEquipamento = (m: Modelo, cidade?: string) => (cidade ? m.cidade(cidade)?.ind.ativos[0] : m.cliente.ativos[0]);
const PASSOS: Passo[] = [
  { texto: "A sala de controle 3D: as cidades do mesmo cliente, ao vivo. No topo, os indicadores do CCO; cada núcleo é uma cidade.", acao: ({ ir }) => ir("/cco") },
  { texto: "O que está anormal fica na frente da cidade, com um feixe de luz. Os setores normais correm no halo por trás do card da cidade.", acao: () => undefined },
  { texto: "O equipamento que mais pede atenção agora fica em destaque, com o motivo ao lado.", acao: (ctx) => {
    ctx.alvo.device = piorEquipamento(ctx.modelo)?.device;
  } },
  { texto: "Zoom na cidade: as outras cidades somem e a câmera entra; os blocos do CCO e as tendências se abrem em volta.", acao: (ctx) => {
    const e = ctx.modelo.equipamento(ctx.alvo.device) ?? piorEquipamento(ctx.modelo);
    ctx.alvo.device = e?.device;
    ctx.ir(urlCidade(e?.cidade ?? ctx.modelo.cidades[0]?.municipio.codigo ?? ""));
  } },
  { texto: "O setor com o alarme: os equipamentos se abrem a partir dele, ligados por linhas de luz.", acao: (ctx) => {
    const e = ctx.modelo.equipamento(ctx.alvo.device);
    if (e) ctx.ir(urlSetor(e.setor));
  } },
  { texto: "A bomba: valores ao vivo e a última hora, num painel ao lado.", acao: (ctx) => { if (ctx.alvo.device) ctx.ir(urlEquip(ctx.alvo.device)); } },
  { texto: "Diagnóstico: as três fases contra a corrente nominal e o poço da bomba submersa (NE, ND, crivo).", acao: (ctx) => { if (ctx.alvo.device) ctx.ir(`${urlEquip(ctx.alvo.device)}/diagnostico`); } },
  { texto: "De volta a todas as cidades. A manobra continua só no CCO, por usuário autorizado.", acao: ({ ir }) => ir("/cco") },
];

function LegendaTour({ ctx, aoSair }: { ctx: CtxTour; aoSair: () => void }) {
  const [i, setI] = useState(0);
  const [pausado, setPausado] = useState(false);
  const barra = useRef<HTMLSpanElement>(null);
  const ctxRef = useRef(ctx);
  ctxRef.current = ctx;
  useEffect(() => { void PASSOS[i].acao(ctxRef.current); }, [i]);
  useEffect(() => {
    if (pausado) return;
    barra.current?.getAnimations().forEach((a) => a.cancel());
    barra.current?.animate([{ width: "0%" }, { width: "100%" }], { duration: 7000, fill: "forwards" });
    const relogio = window.setTimeout(() => setI((x) => (x + 1) % PASSOS.length), 7000);
    return () => window.clearTimeout(relogio);
  }, [i, pausado]);
  return (
    <div className="legenda-tour" role="region" aria-label="Apresentação">
      <span className="passo">{i + 1}/{PASSOS.length}</span><p aria-live="polite">{PASSOS[i].texto}</p>
      <button type="button" className="botao" aria-label="Passo anterior" onClick={() => setI((x) => (x + PASSOS.length - 1) % PASSOS.length)}>‹</button>
      <button type="button" className="botao" aria-label={pausado ? "Continuar" : "Pausar"} onClick={() => setPausado(!pausado)}>{pausado ? "▶" : "❚❚"}</button>
      <button type="button" className="botao" aria-label="Próximo passo" onClick={() => setI((x) => (x + 1) % PASSOS.length)}>›</button>
      <button type="button" className="botao" onClick={aoSair}>Sair</button>
      <span className="barra-tempo" ref={barra} />
    </div>
  );
}

// ---------------------------------------------------------------- tela
export function CcoNovo() {
  const modelo = useModelo();
  const vista = useVista(modelo);
  const navigate = useNavigate();
  const { usuario } = useSessao();
  const chaveModo = `nexora.cco.modo.${usuario?.login ?? "anonimo"}`;
  const [modo, setModo] = useState<Modo>(() => { try { return window.localStorage.getItem(chaveModo) === "3d" ? "3d" : "classico"; } catch { return "classico"; } });
  const [congelado, setCongelado] = useState(false);
  const [reduzido, setReduzido] = useState(lerReduzido);
  const palco = useRef<HTMLDivElement>(null);
  const caixa = useRef<HTMLDivElement>(null);
  const origem = useRef<DOMRect | null>(null);
  const transitando = useRef(false);
  const em3d = modo !== "classico";

  // a tela ocupa a altura que sobra (como o palco do protótipo)
  useLayoutEffect(() => {
    const ajustar = () => { if (caixa.current) caixa.current.style.height = `${Math.max(480, window.innerHeight - caixa.current.getBoundingClientRect().top - 8)}px`; };
    ajustar();
    window.addEventListener("resize", ajustar);
    return () => window.removeEventListener("resize", ajustar);
  }, []);

  const trocarModo = (m: Modo) => { setModo(m); if (m !== "apresentacao") guardar(chaveModo, m); if (m === "apresentacao") navigate("/cco"); };
  const alternarReduzido = () => { const n = !reduzido; setReduzido(n); guardar("nexora.cco.movimentoReduzido", n ? "1" : "0"); };

  // ------------------------------------------------------------ navegação com voo (Clássica)
  // Na hora de navegar, o plano atual vira uma cópia estática (sem iframes, inerte) que sai voando; o plano novo é o
  // único plano real (sempre o da rota). Sem estado de "planos" para sincronizar com a rota.
  const saida = useRef<{ el: HTMLElement; indo: boolean } | null>(null);
  const chave = urlDe(vista);
  const modeloRef = useRef(modelo);
  modeloRef.current = modelo;
  /** Navega (com o voo na Clássica). `sincrono`: ação do usuário — o nível muda na hora, sem esperar a transição. */
  const navegar = useCallback((url: string, sincrono = false) => {
    const atual = palco.current?.querySelector<HTMLElement>(".plano.atual");
    if (!em3d && atual && url !== window.location.pathname) {
      saida.current?.el.remove();
      const copia = atual.cloneNode(true) as HTMLElement;
      copia.classList.replace("atual", "saindo");
      copia.setAttribute("inert", "");
      copia.setAttribute("aria-hidden", "true");
      copia.querySelectorAll("iframe, canvas").forEach((f) => f.replaceWith(document.createElement("div")));
      palco.current!.appendChild(copia);
      copia.scrollTop = atual.scrollTop;
      saida.current = { el: copia, indo: nivelDaUrl(url) > nivelDaUrl(window.location.pathname) };
    }
    navigate(url, sincrono ? { flushSync: true } : undefined);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [em3d, chave, navigate]);

  // ------------------------------------------------------------ zoom (clique, Enter, roda) e volta
  const ir = useCallback(async (url: string, el?: Element | null) => {
    if (transitando.current) return;
    origem.current = el?.getBoundingClientRect() ?? null;
    // Clássica: no zoom da cidade, as outras cidades saem antes do voo
    if (!em3d && !reduzido && el && vista.nivel === 0 && (el as HTMLElement).dataset.zoom === "cidade") {
      const linha = el.closest("[data-linha]")?.getAttribute("data-linha");
      const outras = [...(palco.current?.querySelectorAll("[data-linha]") ?? [])].filter((x) => x.getAttribute("data-linha") !== linha);
      transitando.current = true;
      await Promise.all(outras.map((x) => x.animate([{ opacity: 1, filter: "blur(0)" }, { opacity: 0, filter: "blur(6px)" }], { duration: 360, easing: CURVA, fill: "forwards" }).finished));
      transitando.current = false;
    }
    navegar(url, true);
  }, [em3d, reduzido, vista.nivel, navegar]);
  // a volta parte da URL real do navegador: o estado do React pode estar uma navegação atrás (transição adiada
  // pelas leituras ao vivo que chegam a todo instante)
  const voltar = useCallback(() => {
    const destino = acimaDaUrl(window.location.pathname, modeloRef.current);
    if (destino) { origem.current = null; navegar(destino, true); }
  }, [navegar]);

  useEffect(() => {
    const el = palco.current!;
    const zoomPor = (alvo: EventTarget | null) => {
      const z = (alvo as HTMLElement | null)?.closest?.("[data-zoom]") as HTMLElement | null;
      if (!z?.dataset.href || modo === "apresentacao") return false;
      void ir(z.dataset.href, z);
      return true;
    };
    const clique = (ev: MouseEvent) => { if (zoomPor(ev.target)) ev.preventDefault(); };
    const tecla = (ev: KeyboardEvent) => { if (ev.key === "Enter" && zoomPor(ev.target)) ev.preventDefault(); };
    let acumulado = 0, ultimoGiro = 0;
    const roda = (ev: WheelEvent) => {
      if (modo === "apresentacao") return;
      const sobreCard = (ev.target as HTMLElement).closest("[data-zoom]");
      if (em3d && !sobreCard) return;   // a 3D aproxima a câmera
      const plano = el.querySelector<HTMLElement>(".plano.atual");
      if (!em3d && !sobreCard && !ev.ctrlKey && (ev.deltaY < 0 || (plano && plano.scrollTop + plano.clientHeight < plano.scrollHeight - 2))) return;   // rola a página
      ev.preventDefault();
      const agora = performance.now();
      if (agora - ultimoGiro > 400) acumulado = 0;
      ultimoGiro = agora;
      acumulado += ev.deltaY;
      if (acumulado < -140) { acumulado = 0; if (sobreCard) zoomPor(sobreCard); }
      else if (acumulado > 140) { acumulado = 0; voltar(); }
    };
    el.addEventListener("click", clique);
    el.addEventListener("keydown", tecla);
    el.addEventListener("wheel", roda, { passive: false });
    return () => { el.removeEventListener("click", clique); el.removeEventListener("keydown", tecla); el.removeEventListener("wheel", roda); };
  }, [ir, voltar, em3d, modo]);

  useEffect(() => {
    const tecla = (ev: KeyboardEvent) => {
      const alvo = ev.target as HTMLElement;
      if (alvo?.matches?.("input, textarea, select, [contenteditable]")) return;
      if (document.querySelector(".gaveta-assistente.aberta, [role='alertdialog'], .tela-cheia-grafana")) return;
      if (ev.key === "Escape" && modo === "apresentacao") { trocarModo("3d"); return; }
      if (ev.key === "Escape" || ev.key === "Backspace") { if (nivelDaUrl(window.location.pathname) > 0) { ev.preventDefault(); voltar(); } }
    };
    window.addEventListener("keydown", tecla);
    return () => window.removeEventListener("keydown", tecla);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [voltar, modo]);

  // ------------------------------------------------------------ Clássica: voo pela profundidade
  useLayoutEffect(() => {
    const s0 = saida.current;
    saida.current = null;
    const novo = palco.current?.querySelector<HTMLElement>(".plano.atual");
    if (!s0) return;
    if (!novo) { s0.el.remove(); return; }
    const r = palco.current!.getBoundingClientRect(), o = origem.current;
    const transformOrigin = o ? `${((o.left + o.width / 2 - r.left) / r.width) * 100}% ${((o.top + o.height / 2 - r.top) / r.height) * 100}%` : "50% 50%";
    const dur = reduzido ? 160 : 700;
    const saidaQ = reduzido ? [{ opacity: 1 }, { opacity: 0 }] : [{ transform: "none", opacity: 1, filter: "blur(0)" }, { transform: s0.indo ? "translateZ(700px)" : "translateZ(-900px)", opacity: 0, filter: "blur(5px)" }];
    const entrada = reduzido ? [{ opacity: 0 }, { opacity: 1 }] : [{ transform: s0.indo ? "translateZ(-900px)" : "translateZ(600px)", opacity: 0 }, { transform: "none", opacity: 1 }];
    s0.el.style.transformOrigin = novo.style.transformOrigin = transformOrigin;
    if (!reduzido) novo.querySelectorAll(".grade .card, .painel, .bloco").forEach((c, i) =>
      c.animate([{ transform: "translateZ(-240px)", opacity: 0 }, { transform: "none", opacity: 1 }], { duration: 460, delay: 220 + Math.min(i, 14) * 40, easing: CURVA, fill: "backwards" }));
    novo.animate(entrada, { duration: dur, easing: CURVA });
    const a = s0.el.animate(saidaQ, { duration: dur, easing: CURVA, fill: "forwards" });
    a.finished.catch(() => undefined).then(() => s0.el.remove());
  }, [chave, reduzido]);
  // ao trocar de modo, nenhuma cópia fica para trás
  useEffect(() => { palco.current?.querySelectorAll(".plano.saindo").forEach((x) => x.remove()); }, [em3d]);

  // o foco fica no palco a cada nível (um iframe do Grafana não pode engolir o Esc da navegação)
  useEffect(() => {
    const t = window.setTimeout(() => { if (!palco.current?.contains(document.activeElement) || document.activeElement?.tagName === "IFRAME") palco.current?.focus({ preventScroll: true }); }, 50);
    return () => window.clearTimeout(t);
  }, [chave]);

  // ------------------------------------------------------------ trilha
  const cidadeV = modelo.cidade(vista.cidade), setorV = modelo.setor(vista.setor), equipV = modelo.equipamento(vista.equip);
  const partes: ReactNode[] = [<button key="c" type="button" onClick={() => navegar("/cco", true)}>Cliente · {modelo.cidades.length} cidades</button>];
  if (vista.nivel >= 1 && vista.cidade) partes.push(<span key="s1" className="sep">›</span>, <button key="ci" type="button" onClick={() => navegar(urlCidade(vista.cidade!), true)}>{cidadeV?.municipio.nome ?? vista.cidade}</button>);
  if (vista.nivel >= 2 && vista.setor) partes.push(<span key="s2" className="sep">›</span>, <button key="se" type="button" onClick={() => navegar(urlSetor(vista.setor!), true)}>{setorV?.titulo ?? vista.setor.toUpperCase()}</button>);
  if (vista.nivel >= 3 && vista.equip) partes.push(<span key="s3" className="sep">›</span>, <button key="eq" type="button" onClick={() => navegar(urlEquip(vista.equip!), true)}>{equipV?.nome ?? vista.equip}</button>);
  if (vista.nivel >= 4) partes.push(<span key="s4" className="sep">›</span>, <span key="dg">diagnóstico</span>);

  const ctxTour: CtxTour = { modelo, ir: (url) => navegar(url), alvo: useRef({}).current };
  return (
    <div className={`cco-novo${em3d ? " modo-3d" : ""}`} ref={caixa}>
      <header className="barra">
        <div className="modos" role="group" aria-label="Opção de tela do CCO">
          {([["classico", "Clássico"], ["3d", "3D"], ["apresentacao", "Apresentação"]] as [Modo, string][]).map(([m, rotulo]) => (
            <button key={m} type="button" aria-pressed={modo === m} onClick={() => trocarModo(m)}>{rotulo}</button>))}
        </div>
        <span className="aviso-ficticio">{modelo.cidades.length === 1 ? "1 cidade do cliente · dados reais" : `${modelo.cidades.length} cidades do mesmo cliente`}</span>
        <div className="controles">
          <button type="button" className="botao" aria-pressed={congelado} onClick={() => setCongelado(!congelado)}>{congelado ? "▶ Retomar" : "❚❚ Congelar"}</button>
          <button type="button" className="botao" aria-pressed={reduzido} onClick={alternarReduzido}>Movimento reduzido</button>
        </div>
      </header>
      <nav className="trilha" aria-label="Nível de zoom">{partes}
        <span className="dica">{em3d ? "arraste para girar · roda aproxima e afasta · clique num card abre o nível · Esc volta"
          : "role sobre um card para aproximar · role para trás ou Esc para afastar · clique · Enter"}</span></nav>
      <main className={`palco${em3d ? " em3d" : ""}`} ref={palco} tabIndex={-1} aria-label="Palco do CCO">
        {em3d ? <Cco3d modelo={modelo} nivel={vista.nivel} cidade={vista.cidade} setor={vista.setor} equip={vista.equip} congelado={congelado} reduzido={reduzido} />
          : <div key={chave} className="plano atual" data-nivel-plano={vista.nivel}><Plano vista={vista} modelo={modelo} congelado={congelado} reduzido={reduzido} /></div>}
        {modo === "apresentacao" && <LegendaTour ctx={ctxTour} aoSair={() => trocarModo("3d")} />}
      </main>
    </div>
  );
}
